import { useId, type FocusEventHandler, type KeyboardEventHandler } from 'react';
import { Input as AriaInput, TextArea as AriaTextArea, TextField } from 'react-aria-components';
import { controlClass, Counter, FieldErrorText, FieldHint, FieldLabel, fieldClassName, joinClasses } from './Field';

export interface InputFieldProps {
  label: string;
  hint?: string;
  error?: string;
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  className?: string;
  type?: 'text' | 'email' | 'password';
  name?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  /** Keeps the label in the accessibility tree but hides it visually. */
  labelHidden?: boolean;
  /** `content` sizes a single-line input to its text (with a minimum width), up to the space available. */
  fit?: 'content';
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

// Complete literals: Tailwind emits a utility only if its whole name appears in source.
// text-ellipsis: on a phone the field can only be its min-w-fit-field floor, narrower than a long value;
// an ellipsis shows the value is cut instead of clipping it mid-letter. Focus still scrolls the full text.
const FIT_CONTENT = 'field-sizing-content max-w-full min-w-fit-field text-ellipsis';

function TextControl({
  multiline,
  label,
  hint,
  error,
  value,
  onChange,
  maxLength,
  className,
  type,
  name,
  autoComplete,
  autoFocus,
  isRequired,
  isDisabled,
  labelHidden,
  fit,
  onBlur,
  onKeyDown
}: InputFieldProps & { multiline: boolean }) {
  const counterId = useId();
  const hintId = useId();
  const describedBy = joinClasses(hint ? hintId : null, maxLength !== undefined ? counterId : null) || undefined;
  return (
    <TextField
      value={value}
      onChange={onChange}
      isInvalid={!!error}
      maxLength={maxLength}
      type={type}
      name={name}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      isRequired={isRequired}
      isDisabled={isDisabled}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      aria-describedby={describedBy}
      className={fieldClassName(className)}
    >
      <FieldLabel hidden={labelHidden}>{label}</FieldLabel>
      {multiline ? <AriaTextArea className={`${controlClass} resize-y`} /> : <AriaInput className={fit === 'content' ? `${controlClass} ${FIT_CONTENT}` : controlClass} />}
      <FieldHint hint={hint} id={hintId} />
      {maxLength !== undefined ? <Counter id={counterId} length={value.length} max={maxLength} /> : null}
      <FieldErrorText error={error} />
    </TextField>
  );
}

export function InputField(props: InputFieldProps) {
  return <TextControl multiline={false} {...props} />;
}

export function TextareaField(props: InputFieldProps) {
  return <TextControl multiline {...props} />;
}
